import { cn } from "@gymflow/helpers";
import { motion } from "framer-motion";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { AppPreviewAppointmentsIcon } from "../../atoms/icons/AppPreviewAppointmentsIcon";
import { AppPreviewClassesIcon } from "../../atoms/icons/AppPreviewClassesIcon";
import { AppPreviewHomeIcon } from "../../atoms/icons/AppPreviewHomeIcon";
import { AppPreviewProfileIcon } from "../../atoms/icons/AppPreviewProfileIcon";
import { AppPreviewShopIcon } from "../../atoms/icons/AppPreviewShopIcon";
import { AppSettingsContext } from "./AppSettingsContext";
import preview from "./preview.png";

export const AppSettingsPreview = ({
  imageClassName,
  className,
}: {
  imageClassName?: string;
  className?: string;
}) => {
  const { t } = useTranslation();
  const { navigationLocalData } = useContext(AppSettingsContext);
  if (!navigationLocalData) return null;
  const amountEnabled =
    Object.values(navigationLocalData).filter(Boolean).length + 2;
  return (
    <div className={cn("flex size-full items-center justify-center rounded-3xl bg-gray-100 p-6", className)}>
      <div className="flex h-fit min-w-fit flex-col overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
        <img
          src={preview}
          alt="preview"
          className={cn(
            "flex h-full max-h-full object-contain",
            imageClassName,
          )}
        />
        <motion.div className="flex w-full min-w-fit flex-row items-center justify-around gap-2 border-t border-gray-200 bg-white p-2">
          <Option className="bg-secondary-50 rounded-lg">
            <AppPreviewHomeIcon className="stroke-secondary-600 size-4" />
            {amountEnabled <= 3 && (
              <OptionLabel className="text-secondary-600">
                {t("page.settings.appSettings.navigation.homeIcon")}
              </OptionLabel>
            )}
          </Option>
          {navigationLocalData.classesNavigationEnabled && (
            <Option>
              <AppPreviewClassesIcon className="size-4 fill-gray-500" />
              {amountEnabled <= 3 && (
                <OptionLabel>
                  {t("page.settings.appSettings.navigation.classesIcon")}
                </OptionLabel>
              )}
            </Option>
          )}
          {navigationLocalData.appointmentsNavigationEnabled && (
            <Option>
              <AppPreviewAppointmentsIcon className="size-4 stroke-gray-500" />
              {amountEnabled <= 3 && (
                <OptionLabel>
                  {t("page.settings.appSettings.navigation.appointmentsIcon")}
                </OptionLabel>
              )}
            </Option>
          )}
          {navigationLocalData.shopNavigationEnabled && (
            <Option>
              <AppPreviewShopIcon className="size-4 stroke-gray-500" />
              {amountEnabled <= 3 && (
                <OptionLabel>
                  {t("page.settings.appSettings.navigation.shopIcon")}
                </OptionLabel>
              )}
            </Option>
          )}
          <Option>
            <AppPreviewProfileIcon className="size-4 stroke-gray-500" />
            {amountEnabled <= 3 && (
              <OptionLabel>
                {t("page.settings.appSettings.navigation.profileIcon")}
              </OptionLabel>
            )}
          </Option>
        </motion.div>
      </div>
    </div>
  );
};

const Option = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.div
      layout
      className={cn(
        "flex flex-row items-center gap-1 p-1 py-2 flex-1 justify-center",
        className,
      )}
    >
      {children}
    </motion.div>
  );
};
const OptionLabel = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.div
      className={cn("text-xs font-medium text-gray-500", className)}
      layout
    >
      {children}
    </motion.div>
  );
};
